import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
  unlink
} from 'firebase/auth';
import { auth, googleProvider, initializeRecaptcha } from '../firebase/config';
import { trackSignIn, setAnalyticsUserId, setAnalyticsUserProperties, trackPageView } from '../utils/analytics';

// Use popup on non-production domains (preview/localhost) to avoid redirect issues
const isProductionDomain = typeof window !== 'undefined' && 
  (window.location.hostname === 'pipercard.com' || 
   window.location.hostname === 'www.pipercard.com' ||
   window.location.hostname === 'connectqr-efbe9.firebaseapp.com');

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // Set persistence
        await setPersistence(auth, browserLocalPersistence);
        
        // Check if we just completed a redirect sign-in
        const pendingRedirect = localStorage.getItem('pendingRedirect');
        
        if (pendingRedirect === 'true') {
          console.log('🔍 Checking for redirect result after sign-in...');
          localStorage.removeItem('pendingRedirect');
          
          try {
            const result = await getRedirectResult(auth);
            
            if (result && result.user) {
              console.log('✅ Sign-in successful!', result.user.email);
              
              // Track sign-in event
              trackSignIn('google');
              setAnalyticsUserId(result.user.uid);
              setAnalyticsUserProperties({
                email: result.user.email,
                display_name: result.user.displayName,
              });
              
              if (mounted) {
                setUser(result.user);
                setLoading(false);
              }
              return;
            }
          } catch (error) {
            console.error('❌ Redirect error:', error);
          }
        }

        // Set up auth state listener
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
          if (mounted) {
            if (currentUser) {
              // Set analytics user ID when user state changes
              setAnalyticsUserId(currentUser.uid);
              setAnalyticsUserProperties({
                email: currentUser.email,
                display_name: currentUser.displayName,
              });
              
              // Fetch user profile from database to get custom photoURL
              try {
                const token = await currentUser.getIdToken();
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/profile`, {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                });
                if (response.ok) {
                  const dbProfile = await response.json();
                  // Create merged user object with database values taking priority
                  // Firebase User has getters, so we need to explicitly copy properties
                  const mergedUser = {
                    // Core Firebase properties
                    uid: currentUser.uid,
                    email: currentUser.email,
                    emailVerified: currentUser.emailVerified,
                    isAnonymous: currentUser.isAnonymous,
                    metadata: currentUser.metadata,
                    providerData: currentUser.providerData,
                    refreshToken: currentUser.refreshToken,
                    tenantId: currentUser.tenantId,
                    // Properties that can be overridden by database
                    displayName: dbProfile.displayName || currentUser.displayName,
                    photoURL: dbProfile.photoURL || currentUser.photoURL,
                    phoneNumber: dbProfile.phoneNumber || currentUser.phoneNumber,
                    // Keep Firebase methods accessible
                    getIdToken: currentUser.getIdToken.bind(currentUser),
                    getIdTokenResult: currentUser.getIdTokenResult.bind(currentUser),
                    reload: currentUser.reload.bind(currentUser),
                    toJSON: currentUser.toJSON.bind(currentUser),
                    delete: currentUser.delete.bind(currentUser),
                  };
                  console.log('✅ Merged user profile:', { 
                    photoURL: mergedUser.photoURL?.substring(0, 50) + '...', 
                    displayName: mergedUser.displayName 
                  });
                  setUser(mergedUser);
                } else {
                  setUser(currentUser);
                }
              } catch (error) {
                console.error('Error fetching user profile:', error);
                setUser(currentUser);
              }
            } else {
              setUser(null);
            }
            setLoading(false);
          }
        });

        return unsubscribe;
      } catch (error) {
        console.error('❌ Auth initialization error:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    const unsubscribePromise = initializeAuth();
    
    return () => {
      mounted = false;
      if (unsubscribePromise && typeof unsubscribePromise.then === 'function') {
        unsubscribePromise.then(unsub => {
          if (typeof unsub === 'function') unsub();
        });
      }
    };
  }, []);

  const signInWithGoogle = async () => {
    try {
      console.log('🔐 Initiating Google Sign In...');
      
      if (isProductionDomain) {
        // Use redirect on production domain (returns to same page)
        localStorage.setItem('pendingRedirect', 'true');
        await signInWithRedirect(auth, googleProvider);
      } else {
        // Use popup on preview/localhost to avoid authDomain redirect issues
        console.log('📌 Preview domain detected - using popup sign-in');
        const result = await signInWithPopup(auth, googleProvider);
        if (result && result.user) {
          console.log('✅ Popup sign-in successful!', result.user.email);
          trackSignIn('google');
          setAnalyticsUserId(result.user.uid);
          setAnalyticsUserProperties({
            email: result.user.email,
            display_name: result.user.displayName,
          });
        }
      }
    } catch (error) {
      localStorage.removeItem('pendingRedirect');
      console.error('❌ Sign-in error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Clear user cache before signing out
      if (user) {
        const { clearUserCache } = await import('../services/cachedApi');
        clearUserCache(user.uid);
      }
      
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  // Phone Authentication Methods
  const sendOTP = async (phoneNumber) => {
    try {
      console.log('📱 Sending OTP to:', phoneNumber);
      
      // Initialize recaptcha
      const recaptchaVerifier = initializeRecaptcha('recaptcha-container');
      
      if (!recaptchaVerifier) {
        console.error('❌ RecaptchaVerifier is null');
        throw new Error('Failed to initialize reCAPTCHA. Please refresh the page and try again.');
      }

      console.log('🔐 RecaptchaVerifier ready, calling Firebase...');
      
      // Send OTP
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
      console.log('✅ OTP sent successfully!', confirmationResult);
      
      return confirmationResult;
    } catch (error) {
      console.error('❌ Error sending OTP - Full error:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error message:', error.message);
      
      // Provide more specific error messages
      if (error.code === 'auth/invalid-phone-number') {
        throw new Error('Invalid phone number format. Please enter a valid phone number.');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many attempts. Please try again later.');
      } else if (error.code === 'auth/captcha-check-failed') {
        throw new Error('reCAPTCHA verification failed. Please refresh and try again.');
      } else if (error.code === 'auth/quota-exceeded') {
        throw new Error('SMS quota exceeded. Please contact support.');
      } else if (error.message && error.message.includes('reCAPTCHA')) {
        throw new Error('reCAPTCHA verification failed. Please refresh the page and try again.');
      }
      
      throw error;
    }
  };

  const verifyOTP = async (confirmationResult, otp) => {
    try {
      console.log('🔐 Verifying OTP...');
      
      // Verify OTP
      const result = await confirmationResult.confirm(otp);
      console.log('✅ OTP verified successfully!', result.user.phoneNumber);
      
      // Track sign-in event
      trackSignIn('phone');
      setAnalyticsUserId(result.user.uid);
      setAnalyticsUserProperties({
        phone_number: result.user.phoneNumber,
      });
      
      return result.user;
    } catch (error) {
      console.error('❌ Error verifying OTP:', error);
      throw error;
    }
  };

  // Link Phone Number to Existing Account
  const linkPhoneNumber = async (confirmationResult, otp) => {
    try {
      console.log('🔗 Linking phone number to account...');
      
      if (!user) {
        throw new Error('No user signed in. Please sign in first.');
      }
      
      // Verify OTP and get credential
      const result = await confirmationResult.confirm(otp);
      console.log('✅ Phone number linked successfully!', result.user.phoneNumber);
      
      // Track linking event
      setAnalyticsUserProperties({
        phone_number: result.user.phoneNumber,
        auth_methods: 'google,phone'
      });
      
      return result.user;
    } catch (error) {
      console.error('❌ Error linking phone number:', error);
      
      // Provide specific error messages
      if (error.code === 'auth/credential-already-in-use') {
        throw new Error('This phone number is already linked to another account.');
      } else if (error.code === 'auth/invalid-verification-code') {
        throw new Error('Invalid verification code.');
      } else if (error.code === 'auth/code-expired') {
        throw new Error('Verification code expired. Please request a new one.');
      }
      
      throw error;
    }
  };

  // Unlink Phone Number from Account
  const unlinkPhoneNumber = async () => {
    try {
      console.log('🔓 Unlinking phone number from account...');
      
      if (!user) {
        throw new Error('No user signed in.');
      }

      if (!user.phoneNumber) {
        throw new Error('No phone number linked to this account.');
      }

      // Unlink the phone provider
      await unlink(auth.currentUser, PhoneAuthProvider.PROVIDER_ID);
      console.log('✅ Phone number unlinked successfully!');
      
      // Update analytics
      setAnalyticsUserProperties({
        phone_number: null,
        auth_methods: 'google'
      });
      
      return true;
    } catch (error) {
      console.error('❌ Error unlinking phone number:', error);
      
      // Provide specific error messages
      if (error.code === 'auth/no-such-provider') {
        throw new Error('No phone number is linked to this account.');
      }
      
      throw error;
    }
  };

  const updateUserProfile = async (updates) => {
    try {
      if (!user) {
        throw new Error('No user signed in.');
      }

      // Update in backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const result = await response.json();
      
      // Force reload of auth state by calling the onAuthStateChanged listener
      // This will trigger a re-fetch of user data from backend
      await auth.currentUser.reload();
      
      // Manually update the user state with new data if provided
      if (result.user) {
        setUser(prevUser => ({
          ...prevUser,
          phoneNumber: result.user.phoneNumber || prevUser.phoneNumber,
          photoURL: result.user.photoURL || prevUser.photoURL,
          displayName: result.user.displayName || prevUser.displayName
        }));
      }
      
      return result;
    } catch (error) {
      console.error('❌ Error updating profile:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    signInWithGoogle,
    signOut,
    sendOTP,
    verifyOTP,
    linkPhoneNumber,
    unlinkPhoneNumber,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
